import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { PanelLoading } from "../components/Loading";
import { useTranslation } from "react-i18next";
import { offersApi, type OfferFunnelRow } from "../api/offers";
import { useToast } from "../components/Toast";
import { Select } from "../components/Select";
import { OffersViewSwitcher } from "../components/offers/OffersViewSwitcher";
import type { ApiError } from "../api/client";
import {
  openFunnelRows, wonFunnelRow, lostFunnelRow,
  totalFunnelCount, totalFunnelAmount, conversionToWonPct, barWidthPct,
  type FunnelMetric,
} from "./offerFunnel";
import { sequentialStepColor, STATUS_GOOD, STATUS_CRITICAL } from "./offerPipeline";
import Ic from "../components/icons.jsx";
import { formatMoney } from "../lib/format";

const money = (n: number) => formatMoney(n, "EUR");

/**
 * Sales funnel view — GET
 * /api/offers/funnel rendered as horizontal bars, one per active OPEN stage,
 * plus the two locked WON/LOST outcomes. Route /offers/funnel, third tab of
 * OffersViewSwitcher.
 *
 * Choice of form: "compare magnitude across an ordered set of
 * categories" is the textbook bar-chart-with-sequential-color case, not
 * categorical — the OPEN stages get one hue, light-to-dark, from
 * offerPipeline.ts's validated ramp. WON/LOST aren't part of that ordered
 * progression (they're a *state*, not a further pipeline step), so they get
 * the reserved status colors (good/critical) instead of two more
 * sequential steps, each always paired with an icon + label — a status
 * color is never allowed to carry meaning alone.
 *
 * No filter bar here: unlike the board/list, GET /api/offers/funnel takes no
 * query params — it's a single server-aggregated snapshot of everything the
 * caller can see (owner-scoped for plain SALES, all for staff), so there is
 * nothing client-side to slice further. The shared search box still
 * round-trips through the view switcher's `q` param for when the user jumps
 * back to board/list, it just isn't consumed on this page.
 *
 * Every bar is directly labeled (stage name, count, amount) — with only
 * ~5-8 rows ever on screen this is the normal case for an ordinal category
 * axis (not the "never a number on every point" scatter/line case), so the
 * data is fully reachable without hovering; the hover lift below is an
 * enhancement, not a gate.
 */
export default function OffersFunnelPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const [rows, setRows] = useState<OfferFunnelRow[] | null>(null);
  const [metric, setMetric] = useState<FunnelMetric>("count");

  useEffect(() => {
    offersApi.funnel().then(setRows).catch((e: ApiError) => { toast.error(e.message); setRows([]); });
  }, [toast]);

  const open = useMemo(() => (rows ? openFunnelRows(rows) : []), [rows]);
  const won = rows ? wonFunnelRow(rows) : null;
  const lost = rows ? lostFunnelRow(rows) : null;
  const totalCount = rows ? totalFunnelCount(rows) : 0;
  const totalAmount = rows ? totalFunnelAmount(rows) : 0;
  const conversion = rows ? conversionToWonPct(rows) : 0;

  return (
    <div className="fadein offer-funnel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label="Breadcrumb">
            <span>{t("offers.crumb.commercial")}</span>
            <span>{t("offers.list.title")}</span>
          </nav>
          <h1>{t("offers.list.title")}</h1>
          <div className="sub">{t("offers.funnel.subtitle", "Pipeline volume and conversion, from open stage to won.")}</div>
        </div>
      </div>

      <OffersViewSwitcher active="funnel" search="" />

      {rows === null ? (
        <PanelLoading text={t("common.loading")} />
      ) : rows.length === 0 ? (
        <div style={emptyState}>
          <p style={{ margin: 0, color: "var(--ink-3)" }}>{t("offers.funnel.empty", "No pipeline stages configured yet.")}</p>
        </div>
      ) : (
        <>
          <div style={kpiStrip}>
            <div style={kpiTile}>
              <span style={kpiLabel}>{t("offers.funnel.kpi.total", "Total deals")}</span>
              <strong style={kpiValue}>{totalCount}</strong>
            </div>
            <div style={kpiTile}>
              <span style={kpiLabel}>{t("offers.funnel.kpi.value", "Total value")}</span>
              <strong style={kpiValue}>{money(totalAmount)}</strong>
            </div>
            <div style={kpiTile}>
              <span style={kpiLabel}>{t("offers.funnel.kpi.conversion", "Conversion to won")}</span>
              <strong style={kpiValue}>{conversion}%</strong>
            </div>
          </div>

          <section style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <h2 style={cardTitle}>{t("offers.funnel.pipelineTitle", "Open pipeline")}</h2>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                  {t("offers.funnel.metricLabel", "Bar length")}
                </span>
                <Select<FunnelMetric>
                  value={metric}
                  onChange={setMetric}
                  ariaLabel={t("offers.funnel.metricLabel", "Bar length")}
                  style={{ width: 170 }}
                  options={[
                    { value: "count", label: t("offers.funnel.metric.count", "Deal count") },
                    { value: "amount", label: t("offers.funnel.metric.amount", "Amount (EUR)") },
                  ]}
                />
              </label>
            </div>

            {open.length === 0 ? (
              <p style={{ margin: "12px 0 0", color: "var(--ink-3)", fontSize: 13 }}>
                {t("offers.funnel.noOpenStages", "No open (in-progress) stages configured.")}
              </p>
            ) : (
              // Centered, tapering funnel: each OPEN stage is a symmetric band
              // whose width encodes exactly one metric (count or amount), so it
              // reads as the classic sales-funnel silhouette while every band
              // still shows its own true value — no invented cumulative math.
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 16, alignItems: "center" }}>
                {open.map((row, i) => {
                  const color = sequentialStepColor(i, open.length);
                  const pct = Math.max(14, barWidthPct(row, open, metric)); // floor so a tiny stage stays readable/clickable
                  return (
                    <div
                      key={row.stageId}
                      className="funnel-band"
                      style={{ ...funnelBand, width: `${pct}%`, background: color }}
                      title={`${row.name}: ${t("offers.funnel.rowTitle", "{{count}} deals, {{amount}}", { count: row.count, amount: money(row.totalAmount) })}`}
                    >
                      <span style={funnelBandName}>{row.name}</span>
                      <span style={funnelBandVals}>
                        <b style={{ fontVariantNumeric: "tabular-nums" }}>{row.count}</b>
                        <span style={{ opacity: 0.85, fontVariantNumeric: "tabular-nums" }}>· {money(row.totalAmount)}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {open.length > 0 && (
              <table style={detailTable}>
                <thead>
                  <tr>
                    <th style={{ ...thCell, textAlign: "left" }}>{t("offers.funnel.table.stage", "Stage")}</th>
                    <th style={thCell}>{t("offers.funnel.table.deals", "Deals")}</th>
                    <th style={thCell}>{t("offers.funnel.table.value", "Value")}</th>
                    <th style={thCell}>{t("offers.funnel.table.share", "Share")}</th>
                  </tr>
                </thead>
                <tbody>
                  {open.map((row, i) => (
                    <tr key={row.stageId}>
                      <td style={tdCell}>
                        <span aria-hidden style={{ ...dotSwatch, background: sequentialStepColor(i, open.length) }} />
                        {row.name}
                      </td>
                      <td style={{ ...tdCell, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{row.count}</td>
                      <td style={{ ...tdCell, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(row.totalAmount)}</td>
                      <td style={{ ...tdCell, textAlign: "right", color: "var(--ink-3)", fontVariantNumeric: "tabular-nums" }}>
                        {totalCount > 0 ? Math.round((row.count / totalCount) * 100) : 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <div style={outcomeGrid}>
            <OutcomeTile
              row={won}
              color={STATUS_GOOD}
              icon={<Ic.check />}
              fallbackLabel={t("offers.funnel.won", "Won")}
            />
            <OutcomeTile
              row={lost}
              color={STATUS_CRITICAL}
              icon={<Ic.x />}
              fallbackLabel={t("offers.funnel.lost", "Lost")}
            />
          </div>
        </>
      )}

      <style>{`
        .funnel-band:hover{filter:brightness(1.06)}
      `}</style>
    </div>
  );
}

function OutcomeTile({
  row, color, icon, fallbackLabel,
}: {
  row: OfferFunnelRow | null;
  color: string;
  icon: React.ReactNode;
  fallbackLabel: string;
}) {
  return (
    <div style={{ ...outcomeTile, borderLeftColor: color }}>
      <span aria-hidden style={{ ...outcomeIcon, color }}>{icon}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }}>
        <strong style={{ fontSize: 13, color: "var(--ink)" }}>{row?.name ?? fallbackLabel}</strong>
        <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
          {row ? `${row.count} · ${money(row.totalAmount)}` : "—"}
        </span>
      </div>
    </div>
  );
}

const card: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md)",
  boxShadow: "var(--shadow-1)", padding: 18,
};
const cardTitle: CSSProperties = { margin: 0, fontSize: 14, fontWeight: 600, color: "var(--ink)" };
const kpiStrip: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 };
const kpiTile: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md)", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 4,
  boxShadow: "var(--shadow-1)",
};
const kpiLabel: CSSProperties = { fontSize: 10, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--ink-3)" };
const kpiValue: CSSProperties = { fontSize: 20, color: "var(--ink)", fontVariantNumeric: "tabular-nums" };
const funnelBand: CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
  minWidth: 0, height: 46, padding: "0 16px", borderRadius: 8, color: "var(--on-seq)",
  boxShadow: "var(--shadow-1)", transition: "filter .12s ease, width .25s ease",
};
const funnelBandName: CSSProperties = {
  fontSize: 13.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const funnelBandVals: CSSProperties = { display: "flex", gap: 6, alignItems: "baseline", fontSize: 13, whiteSpace: "nowrap" };
const detailTable: CSSProperties = {
  width: "100%", borderCollapse: "collapse", marginTop: 18, fontSize: 13,
};
const thCell: CSSProperties = {
  textAlign: "right", padding: "8px 10px", borderBottom: "1px solid var(--border)",
  fontSize: 11, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--ink-3)", fontWeight: 600,
};
const tdCell: CSSProperties = { padding: "8px 10px", borderBottom: "1px solid var(--border)", color: "var(--ink-2)" };
const dotSwatch: CSSProperties = {
  display: "inline-block", width: 9, height: 9, borderRadius: "50%", marginRight: 8, verticalAlign: "middle",
};
const outcomeGrid: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 };
const outcomeTile: CSSProperties = {
  display: "flex", alignItems: "center", gap: 10,
  background: "var(--surface)", border: "1px solid var(--border)", borderLeft: "3px solid",
  borderRadius: "var(--r-md)", padding: "12px 14px", boxShadow: "var(--shadow-1)",
};
const outcomeIcon: CSSProperties = {
  display: "inline-flex", width: 20, height: 20, flex: "none",
};
const emptyState: CSSProperties = {
  padding: 32, textAlign: "center",
  background: "var(--surface-2)", border: "1px dashed var(--border-strong)", borderRadius: "var(--r-sm)",
};
